<template>
	<div :class="classes">
		<TeamingTextarea
			class="textarea"
			name="comment"
			:disabled="disabled"
			:placeholder="translate( `placeholder` )"
			:value="comment"
			@input="onTextareaInput"
			@focus="onTextareaFocus"
			@keydown="onTextareaKeydown"
		/>
		<div class="buttons" v-if="showButton">
			<TeamingButton
				:disabled="disabled || !comment"
				@click="onButtonClick"
			>
				{{ translate( `buttonText` ) }}
			</TeamingButton>
		</div>
	</div>
</template>

<script>
import * as KeyCode from "keycode-js";

import {
	FormMixin,
	TeamingTextarea,
	TeamingButton
} from "@teaming/common-ui";

const { CODE_ENTER, CODE_RETURN, } = KeyCode;
const isEnter = event => [ CODE_ENTER, CODE_RETURN, ].includes( event.code );

export default {
	name: "ActivityCommentForm",
	mixins: [ FormMixin, ],
	events: [ "submit", ],
	components: {
		TeamingTextarea,
		TeamingButton,
	},
	props: {
		comment: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			isUp: undefined,
			showButton: false,
			globalClickHandler: null,
		};
	},
	computed: {
		classes() {
			return {
				"activity-comment-form": true,
				"activity-comment-form--up": this.isUp === true,
				"activity-comment-form--down": this.isUp === false,
			};
		},
		// @see: FormMixin
		// eslint-disable-next-line vue/no-unused-properties
		form() {
			return {
				comment: this.comment,
			};
		},
	},
	methods: {
		translate( key, args = {} ) {
			return this.$t( `common-gokr.components.ActivityCommentForm.${ key }`, args );
		},
		open() {
			this.isUp = true;
			this.showButton = true;
		},
		close() {
			this.showButton = false;
			this.isUp = false;
		},
		async onButtonClick() {
			this.$emit( "submit" );
			await this.$nextTick();
			this.close();
		},
		onTextareaInput( e ) {
			const comment = e.target.value;
			this.raiseFormChange( { comment, } );
		},
		async onTextareaKeydown( e ) {
			if ( !e.shiftKey && isEnter( e ) ) {
				e.preventDefault();
				e.stopPropagation();
				this.$emit( "submit" );
				await this.$nextTick();
				return this.close();
			}
		},
		async onTextareaFocus() {
			this.open();
		},
	},
	created() {
		const { global, } = this.$foundation();
		this.globalClickHandler = global.onClickOutside( "activity-comment-form", async () => {
			await this.$nextTick();
			this.close();
		} );
	},
	beforeUnmount() {
		const { global, } = this.$foundation();
		global.offClickOutside( this.globalClickHandler );
		this.globalClickHandler = null;
	},
};
</script>

<style lang="scss" scoped>
@keyframes up {
	from { height: 4.75rem; }
	to { height: 15rem; }
}

@keyframes down {
	from { height: 15rem; }
	to { height: 4.75rem; }
}

.activity-comment-form {
	display: flex;
	flex-direction: column;
	justify-content: space-evenly;
	gap: 1rem;

	padding: 1rem;

	border-top: 0.0625rem solid $borderColor--dark;

	@include for-size('lg') {
		padding: 1rem 2rem;
	}

	&--up {
		animation-name: up;
		animation-duration: 0.5s;
		animation-fill-mode: forwards;
	}

	&--down {
		animation-name: down;
		animation-duration: 0s;
		animation-fill-mode: forwards;
	}
}

.activity-comment-form .textarea {
	flex: 1;
}

.activity-comment-form .buttons {
	display: flex;
	flex-direction: row;
	justify-content: stretch;
	* {
		flex: 1;
	}

	@include for-size('md') {
		* {
			flex: unset;
		}
	}
}
</style>
